import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../api';
import ActiveProductsModal, { type ActiveProductItem } from '../components/ActiveProductsModal';

type TabKey = 'users' | 'notifications' | 'store' | 'products';

interface ProductActivityResponse {
  status?: string;
  config?: {
    active_skus?: string[];
    has_explicit_config?: boolean;
  };
  products?: ActiveProductItem[];
  counts?: {
    products_total?: number;
    selectable_products_total?: number;
    active_skus?: number;
    inactive_skus?: number;
    ozon_hard_excluded?: number;
  };
  ozon_activity?: {
    last_synced_at?: string | null;
    covered_skus?: number;
  };
}

interface UserRow {
  id: number;
  name: string;
  email: string;
  phone: string;
  role_id: number;
  role_name?: string;
  role_level?: number;
}

interface RoleOption {
  id: number;
  name: string;
  level: number;
}

interface NotificationRouteRow {
  id: number;
  recommendationType: string;
  chatIds: string;
}

const OWNER_LEVEL = 15;

const roleLabelByName: Record<string, string> = {
  owner: 'Владелец',
  admin: 'Администратор',
  manager: 'Менеджер',
  user: 'Пользователь',
};

const roleLabelByLevel: Record<number, string> = {
  15: 'Владелец',
  10: 'Администратор',
  5: 'Менеджер',
  1: 'Пользователь',
};

const parseToken = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    return { userId: 0, roleLevel: 0 };
  }
  const [, payload] = token.split('.');
  if (!payload) {
    return { userId: 0, roleLevel: 0 };
  }
  try {
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return {
      userId: Number(decoded.userId || 0),
      roleLevel: Number(decoded.roleLevel || 0),
    };
  } catch (error) {
    console.error(error);
    return { userId: 0, roleLevel: 0 };
  }
};

interface SettingsPageProps {
  initialTab?: TabKey;
  onStoreSaved?: () => void;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ initialTab, onStoreSaved }) => {
  const [tab, setTab] = useState<TabKey>(initialTab || 'users');
  const [users, setUsers] = useState<UserRow[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    roleId: '',
  });
  const [userMessage, setUserMessage] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationTestMessage, setNotificationTestMessage] = useState('');
  const [storeDetailsMessage, setStoreDetailsMessage] = useState('');
  const [storeKeysMessage, setStoreKeysMessage] = useState('');
  const [promoKeysMessage, setPromoKeysMessage] = useState('');
  const [sellmonitorKeysMessage, setSellmonitorKeysMessage] = useState('');
  const [notificationForm, setNotificationForm] = useState({
    tgBotToken: '',
    tgChatIds: '',
  });
  const [notificationTestForm, setNotificationTestForm] = useState({
    recommendationType: '',
    message: '',
  });
  const [hasSavedBotToken, setHasSavedBotToken] = useState(false);
  const [sendingNotificationTest, setSendingNotificationTest] = useState(false);
  const [notificationRoutes, setNotificationRoutes] = useState<NotificationRouteRow[]>([]);
  const routeIdRef = useRef(1);
  const [hasSavedSellmonitorToken, setHasSavedSellmonitorToken] = useState(false);
  const [storeForm, setStoreForm] = useState({
    storeName: '',
    clientId: '',
    apiKey: '',
    promoClientId: '',
    promoClientSecret: '',
    sellmonitorBaseUrl: '',
    sellmonitorApiToken: '',
    taxRate: '',
    defaultGrouping: 'products',
  });

  const [productActivity, setProductActivity] = useState<ProductActivityResponse | null>(null);
  const [productActivityLoading, setProductActivityLoading] = useState(false);
  const [productActivitySaving, setProductActivitySaving] = useState(false);
  const [productActivitySyncing, setProductActivitySyncing] = useState(false);
  const [productActivityError, setProductActivityError] = useState('');
  const [productActivityMessage, setProductActivityMessage] = useState('');
  const [productActivityModalOpen, setProductActivityModalOpen] = useState(false);

  const authInfo = useMemo(() => parseToken(), []);

  const canManageUsers = authInfo.roleLevel >= OWNER_LEVEL;
  const ownerCount = users.filter(user => (user.role_level || 0) >= OWNER_LEVEL).length;

  const loadUsers = async () => {
    const res = await apiFetch('/api/users');
    if (res.ok) {
      const json = await res.json();
      setUsers(json);
    }
  };

  const loadRoles = async () => {
    const res = await apiFetch('/api/roles');
    if (res.ok) {
      const json = await res.json();
      setRoles(json);
    }
  };

  const loadStoreSettings = async () => {
    const res = await apiFetch('/api/settings/store');
    if (res.ok) {
      const json = await res.json();
      setStoreForm(current => ({
        ...current,
        storeName: json.store_name ?? '',
        taxRate: json.tax_rate ?? '',
        defaultGrouping: json.default_grouping ?? 'products',
        sellmonitorBaseUrl: json.sellmonitor_base_url ?? '',
        sellmonitorApiToken: '',
      }));
      setHasSavedSellmonitorToken(Boolean(json.has_sellmonitor_api_token));
      if (typeof window !== 'undefined') {
        localStorage.setItem('storeName', json.store_name ?? '');
        window.dispatchEvent(new Event('storeNameChanged'));
      }
    }
  };

  const loadNotificationSettings = async () => {
    const res = await apiFetch('/api/settings/notifications');
    if (!res.ok) return;
    const json = await res.json().catch(() => ({}));
    const byType = (json?.tg_chat_ids_by_type && typeof json.tg_chat_ids_by_type === 'object')
      ? json.tg_chat_ids_by_type
      : {};
    const rows: NotificationRouteRow[] = Object.entries(byType).map(([recommendationType, chatIds]) => ({
      id: routeIdRef.current++,
      recommendationType: String(recommendationType || ''),
      chatIds: String(chatIds || ''),
    }));
    setNotificationRoutes(rows);
    setHasSavedBotToken(Boolean(json?.has_bot_token));
    setNotificationForm(current => ({
      ...current,
      tgChatIds: String(json?.tg_chat_ids || ''),
      tgBotToken: '',
    }));
  };

  const loadProductActivity = async () => {
    setProductActivityLoading(true);
    setProductActivityError('');
    try {
      const res = await apiFetch('/api/settings/product-activity');
      if (!res.ok) {
        throw new Error(`Ошибка загрузки активных товаров: ${res.status}`);
      }
      const payload = (await res.json()) as ProductActivityResponse;
      setProductActivity(payload && typeof payload === 'object' ? payload : null);
    } catch (err) {
      setProductActivityError(err instanceof Error ? err.message : 'Не удалось загрузить активные товары');
      setProductActivity(null);
    } finally {
      setProductActivityLoading(false);
    }
  };

  const saveProductActivity = async (nextActiveSkus: string[]) => {
    setProductActivitySaving(true);
    setProductActivityError('');
    setProductActivityMessage('');
    try {
      const res = await apiFetch('/api/settings/product-activity', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activeSkus: nextActiveSkus }),
      });
      if (!res.ok) {
        throw new Error(`Ошибка сохранения: ${res.status}`);
      }
      const payload = (await res.json()) as ProductActivityResponse;
      setProductActivity(payload && typeof payload === 'object' ? payload : null);
      setProductActivityMessage('Активные товары сохранены');
      setProductActivityModalOpen(false);
    } catch (err) {
      setProductActivityError(err instanceof Error ? err.message : 'Не удалось сохранить активные товары');
    } finally {
      setProductActivitySaving(false);
    }
  };

  const syncOzonProductActivity = async () => {
    setProductActivitySyncing(true);
    setProductActivityError('');
    setProductActivityMessage('');
    try {
      const res = await apiFetch('/api/settings/product-activity/sync-ozon', { method: 'POST' });
      if (!res.ok) {
        throw new Error(`Ошибка запуска синхронизации: ${res.status}`);
      }
      setProductActivityMessage('Синхронизация с Ozon поставлена в очередь. Обновление данных займёт несколько минут.');
    } catch (err) {
      setProductActivityError(err instanceof Error ? err.message : 'Не удалось запустить синхронизацию');
    } finally {
      setProductActivitySyncing(false);
    }
  };

  useEffect(() => {
    loadUsers().catch(console.error);
    loadRoles().catch(console.error);
    loadStoreSettings().catch(console.error);
    loadNotificationSettings().catch(console.error);
  }, []);

  useEffect(() => {
    if (tab === 'products' && !productActivity && !productActivityLoading) {
      loadProductActivity().catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (initialTab) {
      setTab(initialTab);
    }
  }, [initialTab]);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      name: '',
      email: '',
      phone: '',
      password: '',
      roleId: '',
    });
  };

  const roleLabel = (role?: RoleOption, user?: UserRow) => {
    if (role) {
      return roleLabelByName[role.name] || roleLabelByLevel[role.level] || role.name;
    }
    if (user?.role_name) {
      return roleLabelByName[user.role_name] || roleLabelByLevel[user.role_level || 0] || user.role_name;
    }
    if (user?.role_level) {
      return roleLabelByLevel[user.role_level] || `Роль ${user.role_level}`;
    }
    return '—';
  };

  const roleOptions = roles
    .filter(role => authInfo.roleLevel === OWNER_LEVEL || role.level < OWNER_LEVEL)
    .sort((a, b) => b.level - a.level);

  const handleEdit = (user: UserRow) => {
    setEditingId(user.id);
    setForm({
      name: user.name,
      email: user.email,
      phone: user.phone,
      password: '',
      roleId: String(user.role_id),
    });
    setUserMessage('');
    setIsUserModalOpen(true);
  };

  const submitUser = async (event: React.FormEvent) => {
    event.preventDefault();
    setUserMessage('');
    if (!canManageUsers) {
      setUserMessage('Недостаточно прав для изменения пользователей.');
      return;
    }
    const normalizedName = form.name.trim().toLowerCase();
    const normalizedEmail = form.email.trim().toLowerCase();
    const hasNameDuplicate = users.some(
      user => user.id !== editingId && user.name.trim().toLowerCase() === normalizedName
    );
    if (hasNameDuplicate) {
      setUserMessage('Пользователь с таким именем уже существует.');
      return;
    }
    const hasEmailDuplicate = users.some(
      user => user.id !== editingId && user.email.trim().toLowerCase() === normalizedEmail
    );
    if (hasEmailDuplicate) {
      setUserMessage('Пользователь с такой почтой уже существует.');
      return;
    }
    const payload: Record<string, unknown> = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      role_id: Number(form.roleId),
    };
    if (form.password.trim()) {
      payload.password = form.password.trim();
    }
    const res = await apiFetch(editingId ? `/api/users/${editingId}` : '/api/users', {
      method: editingId ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      await loadUsers();
      setUserMessage(editingId ? 'Пользователь обновлён.' : 'Пользователь добавлен.');
      resetForm();
      setIsUserModalOpen(false);
    } else {
      const error = await res.json().catch(() => ({}));
      setUserMessage(error.detail || 'Не удалось сохранить пользователя.');
    }
  };

  const deleteUser = async (user: UserRow) => {
    setUserMessage('');
    const res = await apiFetch(`/api/users/${user.id}`, { method: 'DELETE' });
    if (res.ok) {
      await loadUsers();
      setUserMessage('Пользователь удалён.');
    } else {
      const error = await res.json().catch(() => ({}));
      setUserMessage(error.detail || 'Не удалось удалить пользователя.');
    }
  };

  const isOwner = (user: UserRow) => (user.role_level || 0) >= OWNER_LEVEL;
  const canEditUser = (user: UserRow) => {
    if (!canManageUsers) return false;
    if (authInfo.roleLevel < OWNER_LEVEL && isOwner(user)) return false;
    return true;
  };

  const canDeleteUser = (user: UserRow) => {
    if (!canEditUser(user)) return false;
    if (authInfo.userId === user.id) return false;
    if (isOwner(user) && ownerCount <= 1) return false;
    return true;
  };

  const submitNotifications = async (event: React.FormEvent) => {
    event.preventDefault();
    setNotificationMessage('');
    if (!hasSavedBotToken && !notificationForm.tgBotToken.trim()) {
      setNotificationMessage('Укажите TG_BOT_TOKEN для первичной настройки уведомлений.');
      return;
    }
    const byTypePayload: Record<string, string> = {};
    for (const route of notificationRoutes) {
      const recommendationType = route.recommendationType.trim().toLowerCase();
      const chatIds = route.chatIds.trim();
      if (!recommendationType || !chatIds) continue;
      byTypePayload[recommendationType] = chatIds;
    }
    const body: Record<string, unknown> = {
      TG_CHAT_IDS: notificationForm.tgChatIds,
      TG_CHAT_IDS_BY_TYPE: byTypePayload,
    };
    if (notificationForm.tgBotToken.trim()) {
      body.TG_BOT_TOKEN = notificationForm.tgBotToken.trim();
    }
    const res = await apiFetch('/api/settings/notifications', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      setNotificationMessage('Данные Telegram сохранены.');
      setNotificationForm(current => ({ ...current, tgBotToken: '' }));
      setHasSavedBotToken(true);
      await loadNotificationSettings();
    } else {
      const error = await res.json().catch(() => ({}));
      setNotificationMessage(error.detail || 'Не удалось сохранить данные.');
    }
  };

  const sendNotificationTest = async () => {
    setNotificationTestMessage('');
    if (!hasSavedBotToken) {
      setNotificationTestMessage('Сначала сохраните TG_BOT_TOKEN.');
      return;
    }
    const body: Record<string, unknown> = {};
    const recommendationType = notificationTestForm.recommendationType.trim();
    const message = notificationTestForm.message.trim();
    if (recommendationType) {
      body.recommendation_type = recommendationType;
    }
    if (message) {
      body.message = message;
    }
    try {
      setSendingNotificationTest(true);
      const res = await apiFetch('/api/settings/notifications/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setNotificationTestMessage(json?.detail || 'Не удалось отправить тест.');
        return;
      }
      const routeKey = String(json?.route_key || 'TG_CHAT_IDS');
      const delivered = Number(json?.stats?.delivered || 0);
      const targetChats = Number(json?.stats?.target_chats || 0);
      setNotificationTestMessage(
        `Тест отправлен (${delivered}/${targetChats}), маршрут: ${routeKey}.`
      );
    } catch (error) {
      setNotificationTestMessage('Не удалось отправить тест.');
      console.error(error);
    } finally {
      setSendingNotificationTest(false);
    }
  };

  const addNotificationRoute = () => {
    setNotificationRoutes(current => [
      ...current,
      {
        id: routeIdRef.current++,
        recommendationType: '',
        chatIds: '',
      },
    ]);
  };

  const updateNotificationRoute = (
    routeId: number,
    patch: Partial<Pick<NotificationRouteRow, 'recommendationType' | 'chatIds'>>
  ) => {
    setNotificationRoutes(current =>
      current.map(route => (route.id === routeId ? { ...route, ...patch } : route))
    );
  };

  const removeNotificationRoute = (routeId: number) => {
    setNotificationRoutes(current => current.filter(route => route.id !== routeId));
  };

  const saveStoreSettings = async (
    payload: Record<string, unknown>,
    successMessage: string,
    setMessage: (value: string) => void
  ) => {
    setMessage('');
    const res = await apiFetch('/api/settings/store', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      setMessage(successMessage);
      loadStoreSettings().catch(console.error);
      if (onStoreSaved) {
        onStoreSaved();
      }
      return;
    }
    const error = await res.json().catch(() => ({}));
    setMessage(error.detail || 'Не удалось сохранить данные.');
  };

  const submitStoreDetails = async (event: React.FormEvent) => {
    event.preventDefault();
    await saveStoreSettings(
      {
        store_name: storeForm.storeName,
        tax_rate: storeForm.taxRate,
        default_grouping: storeForm.defaultGrouping,
      },
      'Данные магазина сохранены.',
      setStoreDetailsMessage
    );
  };

  const submitStoreKeys = async (event: React.FormEvent) => {
    event.preventDefault();
    await saveStoreSettings(
      {
        client_id: storeForm.clientId,
        api_key: storeForm.apiKey,
      },
      'Ключи Seller API сохранены.',
      setStoreKeysMessage
    );
  };

  const submitPromoKeys = async (event: React.FormEvent) => {
    event.preventDefault();
    await saveStoreSettings(
      {
        promo_client_id: storeForm.promoClientId,
        promo_client_secret: storeForm.promoClientSecret,
      },
      'Ключи Performance API сохранены.',
      setPromoKeysMessage
    );
  };

  const submitSellmonitorKeys = async (event: React.FormEvent) => {
    event.preventDefault();
    const payload: Record<string, unknown> = {
      sellmonitor_base_url: storeForm.sellmonitorBaseUrl,
    };
    if (storeForm.sellmonitorApiToken.trim()) {
      payload.sellmonitor_api_token = storeForm.sellmonitorApiToken.trim();
    }
    await saveStoreSettings(
      payload,
      'Данные Sellmonitor сохранены.',
      setSellmonitorKeysMessage
    );
    if (storeForm.sellmonitorApiToken.trim()) {
      setHasSavedSellmonitorToken(true);
      setStoreForm(current => ({ ...current, sellmonitorApiToken: '' }));
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen p-6">
      {isUserModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                {editingId ? 'Редактирование пользователя' : 'Добавление пользователя'}
              </h2>
              <button
                type="button"
                className="text-slate-400 hover:text-slate-600"
                onClick={() => {
                  setIsUserModalOpen(false);
                  resetForm();
                }}
                aria-label="Закрыть"
              >
                ✕
              </button>
            </div>
            {!canManageUsers && (
              <p className="text-sm text-red-500 mb-4">
                Недостаточно прав для управления пользователями.
              </p>
            )}
            <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={submitUser}>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Имя</label>
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={form.name}
                  onChange={event => setForm({ ...form, name: event.target.value })}
                  required
                  disabled={!canManageUsers}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  className="w-full border rounded-lg px-3 py-2"
                  value={form.email}
                  onChange={event => setForm({ ...form, email: event.target.value })}
                  required
                  disabled={!canManageUsers}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Телефон</label>
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={form.phone}
                  onChange={event => setForm({ ...form, phone: event.target.value })}
                  required
                  disabled={!canManageUsers}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  {editingId ? 'Новый пароль' : 'Пароль'}
                </label>
                <input
                  type="password"
                  className="w-full border rounded-lg px-3 py-2"
                  value={form.password}
                  onChange={event => setForm({ ...form, password: event.target.value })}
                  disabled={!canManageUsers}
                  required={!editingId}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Роль</label>
                <select
                  className="w-full border rounded-lg px-3 py-2"
                  value={form.roleId}
                  onChange={event => setForm({ ...form, roleId: event.target.value })}
                  required
                  disabled={!canManageUsers}
                >
                  <option value="" disabled>
                    Выберите роль
                  </option>
                  {roleOptions.map(role => (
                    <option key={role.id} value={role.id}>
                      {roleLabel(role)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end gap-2">
                <button
                  type="submit"
                  className={`px-4 py-2 rounded-lg text-white ${
                    canManageUsers ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-300'
                  }`}
                  disabled={!canManageUsers}
                >
                  {editingId ? 'Сохранить' : 'Добавить'}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300"
                  onClick={() => {
                    setIsUserModalOpen(false);
                    resetForm();
                  }}
                >
                  Отменить
                </button>
              </div>
              {userMessage && (
                <div className="md:col-span-2 text-sm text-gray-600">{userMessage}</div>
              )}
            </form>
          </div>
        </div>
      )}
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">Настройки</h1>
          <div />
        </div>

        <div className="flex gap-2 border-b mb-6">
          {([
            { key: 'users', label: 'Пользователи' },
            { key: 'notifications', label: 'Оповещение' },
            { key: 'store', label: 'Магазин' },
            { key: 'products', label: 'Товары' },
          ] as { key: TabKey; label: string }[]).map(item => (
            <button
              key={item.key}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                tab === item.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setTab(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {tab === 'users' && (
          <div className="space-y-6">
            <div className="overflow-auto rounded-xl border">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Имя</th>
                    <th className="px-4 py-3 text-left font-semibold">Email</th>
                    <th className="px-4 py-3 text-left font-semibold">Телефон</th>
                    <th className="px-4 py-3 text-left font-semibold">Роль</th>
                    <th className="px-4 py-3 text-right font-semibold">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} className="border-t">
                      <td className="px-4 py-3">{user.name}</td>
                      <td className="px-4 py-3">{user.email}</td>
                      <td className="px-4 py-3">{user.phone}</td>
                      <td className="px-4 py-3">{roleLabel(undefined, user)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            className={`px-3 py-1 rounded text-xs ${
                              canEditUser(user)
                                ? 'bg-blue-500 text-white hover:bg-blue-600'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                            onClick={() => handleEdit(user)}
                            disabled={!canEditUser(user)}
                          >
                            Редактировать
                          </button>
                          <button
                            className={`px-3 py-1 rounded text-xs ${
                              canDeleteUser(user)
                                ? 'bg-red-500 text-white hover:bg-red-600'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                            onClick={() => deleteUser(user)}
                            disabled={!canDeleteUser(user)}
                          >
                            Удалить
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!users.length && (
                    <tr>
                      <td className="px-4 py-6 text-center text-gray-500" colSpan={5}>
                        Пользователи не найдены.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {userMessage && (
              <p className="text-sm text-gray-600">{userMessage}</p>
            )}

            <div className="flex items-center justify-end">
              <button
                type="button"
                className={`px-4 py-2 rounded-lg text-white ${
                  canManageUsers ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-300'
                }`}
                disabled={!canManageUsers}
                onClick={() => {
                  resetForm();
                  setUserMessage('');
                  setIsUserModalOpen(true);
                }}
              >
                Добавить пользователя
              </button>
            </div>
          </div>
        )}

        {tab === 'notifications' && (
          <div className="max-w-2xl">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Telegram-оповещения</h2>
            <form className="space-y-4" onSubmit={submitNotifications}>
              <div>
                <label className="block text-sm text-gray-600 mb-1">TG_BOT_TOKEN</label>
                <input
                  type="password"
                  className="w-full border rounded-lg px-3 py-2"
                  value={notificationForm.tgBotToken}
                  onChange={event =>
                    setNotificationForm({ ...notificationForm, tgBotToken: event.target.value })
                  }
                  placeholder={hasSavedBotToken ? 'Токен сохранен. Введите только для замены' : ''}
                />
                {hasSavedBotToken && (
                  <p className="mt-1 text-xs text-slate-500">
                    Токен уже сохранен. Поле можно оставить пустым, чтобы не менять его.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">TG_CHAT_IDS</label>
                <input
                  type="password"
                  className="w-full border rounded-lg px-3 py-2"
                  value={notificationForm.tgChatIds}
                  onChange={event =>
                    setNotificationForm({ ...notificationForm, tgChatIds: event.target.value })
                  }
                />
                <p className="mt-1 text-xs text-slate-500">
                  Общий fallback-канал для всех типов рекомендаций.
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm text-gray-600">Каналы по типам рекомендаций</label>
                  <button
                    type="button"
                    className="px-3 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs hover:bg-slate-200"
                    onClick={addNotificationRoute}
                  >
                    Добавить тип
                  </button>
                </div>
                <div className="overflow-x-auto rounded-lg border">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Тип рекомендации</th>
                        <th className="px-3 py-2 text-left font-medium">TG_CHAT_IDS</th>
                        <th className="px-3 py-2 text-right font-medium">Действие</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notificationRoutes.map(route => (
                        <tr key={route.id} className="border-t">
                          <td className="px-3 py-2">
                            <input
                              className="w-full border rounded-md px-2 py-1"
                              value={route.recommendationType}
                              onChange={event =>
                                updateNotificationRoute(route.id, { recommendationType: event.target.value })
                              }
                              placeholder="price / purchase / ads / content"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              className="w-full border rounded-md px-2 py-1"
                              value={route.chatIds}
                              onChange={event =>
                                updateNotificationRoute(route.id, { chatIds: event.target.value })
                              }
                              placeholder="-1001111111111,-1002222222222"
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              className="px-2 py-1 rounded bg-red-50 text-red-600 text-xs hover:bg-red-100"
                              onClick={() => removeNotificationRoute(route.id)}
                            >
                              Удалить
                            </button>
                          </td>
                        </tr>
                      ))}
                      {notificationRoutes.length === 0 && (
                        <tr>
                          <td className="px-3 py-3 text-slate-500" colSpan={3}>
                            Нет отдельных маршрутов. Будет использоваться общий `TG_CHAT_IDS`.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Если для типа канал не задан, уведомление уйдет в общий `TG_CHAT_IDS`.
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
                <p className="text-sm font-medium text-slate-700">Тест отправки уведомления</p>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Тип рекомендации (необязательно)</label>
                  <input
                    className="w-full border rounded-md px-2 py-1"
                    value={notificationTestForm.recommendationType}
                    onChange={event =>
                      setNotificationTestForm(current => ({
                        ...current,
                        recommendationType: event.target.value,
                      }))
                    }
                    placeholder="price / purchase / ads / content"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Текст сообщения (необязательно)</label>
                  <textarea
                    className="w-full border rounded-md px-2 py-1 min-h-[80px]"
                    value={notificationTestForm.message}
                    onChange={event =>
                      setNotificationTestForm(current => ({
                        ...current,
                        message: event.target.value,
                      }))
                    }
                    placeholder="Если пусто, отправится служебное тестовое сообщение."
                  />
                </div>
                <button
                  type="button"
                  className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50"
                  onClick={() => sendNotificationTest().catch(console.error)}
                  disabled={sendingNotificationTest}
                >
                  {sendingNotificationTest ? 'Отправка теста...' : 'Отправить тест'}
                </button>
                {notificationTestMessage && (
                  <p className="text-xs text-slate-600">{notificationTestMessage}</p>
                )}
              </div>
              <button
                type="submit"
                className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
              >
                Сохранить
              </button>
            </form>
            {notificationMessage && (
              <p className="mt-3 text-sm text-gray-600">{notificationMessage}</p>
            )}
          </div>
        )}

        {tab === 'store' && (
          <div className="max-w-4xl space-y-6">
            <div className="rounded-2xl border bg-white p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">Данные магазина</h2>
                <span className="text-xs text-slate-500">Общие параметры магазина</span>
              </div>
              <form className="space-y-4" onSubmit={submitStoreDetails}>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Название магазина</label>
                  <input
                    type="text"
                    className="w-full border rounded-lg px-3 py-2"
                    value={storeForm.storeName}
                    onChange={event => setStoreForm({ ...storeForm, storeName: event.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Налог, %</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full border rounded-lg px-3 py-2"
                    value={storeForm.taxRate}
                    onChange={event => setStoreForm({ ...storeForm, taxRate: event.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Группировка по умолчанию</label>
                  <select
                    className="w-full border rounded-lg px-3 py-2"
                    value={storeForm.defaultGrouping}
                    onChange={event =>
                      setStoreForm({ ...storeForm, defaultGrouping: event.target.value })
                    }
                  >
                    <option value="products">по товарам</option>
                    <option value="groups">по категориям</option>
                  </select>
                </div>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
                >
                  Сохранить
                </button>
                {storeDetailsMessage && (
                  <p className="text-sm text-gray-600">{storeDetailsMessage}</p>
                )}
              </form>
            </div>

            <div className="rounded-2xl border bg-white p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">Ключи доступа Ozon</h2>
                <span className="text-xs text-slate-500">Seller API и реклама</span>
              </div>
              <div className="mb-6 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-slate-700">
                <div className="text-sm font-semibold text-slate-800 mb-2">
                  Как получить ключи Seller API
                </div>
                <p className="mb-3">
                  Для работы дашборда нужны <span className="font-semibold">Client ID</span> и{' '}
                  <span className="font-semibold">API_KEY</span>.
                </p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>
                    Откройте{' '}
                    <a
                      href="https://seller.ozon.ru/app/settings/api-keys"
                      className="text-blue-600 underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      https://seller.ozon.ru/app/settings/api-keys
                    </a>
                    .
                  </li>
                  <li>Нажмите «Сгенерировать ключ».</li>
                  <li>Выберите роль «Admin read only» (только чтение).</li>
                  <li>Нажмите «Сгенерировать» и скопируйте ключ.</li>
                  <li>Вставьте ключ в поле <span className="font-semibold">API_KEY</span>.</li>
                  <li>
                    Укажите <span className="font-semibold">Client ID</span> из раздела Seller API.
                  </li>
                </ol>
              </div>
              <form className="space-y-4" onSubmit={submitStoreKeys}>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Client ID</label>
                  <input
                    type="password"
                    className="w-full border rounded-lg px-3 py-2"
                    value={storeForm.clientId}
                    onChange={event => setStoreForm({ ...storeForm, clientId: event.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">API_KEY</label>
                  <input
                    type="password"
                    className="w-full border rounded-lg px-3 py-2"
                    value={storeForm.apiKey}
                    onChange={event => setStoreForm({ ...storeForm, apiKey: event.target.value })}
                  />
                </div>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
                >
                  Сохранить
                </button>
                {storeKeysMessage && (
                  <p className="text-sm text-gray-600">{storeKeysMessage}</p>
                )}
              </form>
              <div className="mt-6 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-slate-700">
                <div className="text-sm font-semibold text-slate-800 mb-2">
                  Как получить ключи для рекламных акций
                </div>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>
                    Перейдите по ссылке{' '}
                    <a
                      href="https://seller.ozon.ru/app/settings/performance-api"
                      className="text-blue-600 underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      https://seller.ozon.ru/app/settings/performance-api
                    </a>
                    .
                  </li>
                  <li>Нажмите кнопку «создать аккаунт».</li>
                  <li>Нажмите на создавшийся сервисный аккаунт — откроется модальное окно.</li>
                  <li>
                    Нажмите «Добавить ключ». Введите название ключа — <span className="font-semibold">Bertbit</span>.
                  </li>
                  <li>
                    Скопируйте <span className="font-semibold">client id</span> и вставьте в поле{' '}
                    <span className="font-semibold">PROMO_CLIENT_ID</span>.
                  </li>
                  <li>
                    Скопируйте <span className="font-semibold">client_secret</span> и вставьте в поле{' '}
                    <span className="font-semibold">PROMO_CLIENT_SECRET</span>.
                  </li>
                  <li>Нажмите «Закрыть» в модальном окне.</li>
                </ol>
              </div>
              <form className="space-y-4 mt-4" onSubmit={submitPromoKeys}>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">PROMO_CLIENT_ID</label>
                  <input
                    type="password"
                    className="w-full border rounded-lg px-3 py-2"
                    value={storeForm.promoClientId}
                    onChange={event =>
                      setStoreForm({ ...storeForm, promoClientId: event.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">PROMO_CLIENT_SECRET</label>
                  <input
                    type="password"
                    className="w-full border rounded-lg px-3 py-2"
                    value={storeForm.promoClientSecret}
                    onChange={event =>
                      setStoreForm({ ...storeForm, promoClientSecret: event.target.value })
                    }
                  />
                </div>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
                >
                  Сохранить
                </button>
                {promoKeysMessage && (
                  <p className="text-sm text-gray-600">{promoKeysMessage}</p>
                )}
              </form>
              <div className="mt-6 rounded-xl border border-violet-100 bg-violet-50 p-4 text-sm text-slate-700">
                <div className="text-sm font-semibold text-slate-800 mb-2">
                  Интеграция Sellmonitor
                </div>
                <p>
                  Укажите base URL и API token агрегатора конкурентных данных.
                  Эти данные используются для ingestion цен/наличия/позиций и quality-checks.
                </p>
              </div>
              <form className="space-y-4 mt-4" onSubmit={submitSellmonitorKeys}>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">SELLMONITOR_BASE_URL</label>
                  <input
                    type="url"
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="https://api.sellmonitor.example"
                    value={storeForm.sellmonitorBaseUrl}
                    onChange={event =>
                      setStoreForm({ ...storeForm, sellmonitorBaseUrl: event.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">SELLMONITOR_API_TOKEN</label>
                  <input
                    type="password"
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder={hasSavedSellmonitorToken ? 'Токен сохранен. Введите только для замены' : ''}
                    value={storeForm.sellmonitorApiToken}
                    onChange={event =>
                      setStoreForm({ ...storeForm, sellmonitorApiToken: event.target.value })
                    }
                  />
                  {hasSavedSellmonitorToken && (
                    <p className="mt-1 text-xs text-slate-500">
                      Токен уже сохранен. Поле можно оставить пустым, чтобы не менять его.
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
                >
                  Сохранить
                </button>
                {sellmonitorKeysMessage && (
                  <p className="text-sm text-gray-600">{sellmonitorKeysMessage}</p>
                )}
              </form>
            </div>
          </div>
        )}

        {tab === 'products' && (
          <div className="max-w-4xl space-y-6">
            <div className="rounded-2xl border bg-white p-5">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Активные товары</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Управление SKU, которые участвуют в выгрузках, AI агенте, срезах,
                    рекомендациях поставок и отображении на дашборде. Товары, архивированные
                    продавцом в Ozon, автоматически исключаются и не могут быть активированы
                    вручную.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setProductActivityModalOpen(true)}
                  disabled={productActivityLoading || !productActivity}
                  className="shrink-0 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:bg-slate-400"
                >
                  Открыть модальное окно
                </button>
              </div>

              {productActivityLoading && (
                <p className="text-sm text-slate-500">Загрузка…</p>
              )}
              {productActivityError && (
                <p className="text-sm text-red-600">{productActivityError}</p>
              )}
              {productActivityMessage && (
                <p className="text-sm text-emerald-600">{productActivityMessage}</p>
              )}

              {productActivity && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                  <div className="rounded-xl border p-4 bg-slate-50">
                    <div className="text-xs uppercase text-slate-500 tracking-wide">Активные SKU</div>
                    <div className="text-2xl font-semibold text-slate-900 mt-1">
                      {productActivity.counts?.active_skus ?? 0}
                    </div>
                  </div>
                  <div className="rounded-xl border p-4 bg-slate-50">
                    <div className="text-xs uppercase text-slate-500 tracking-wide">Всего управляемых SKU</div>
                    <div className="text-2xl font-semibold text-slate-900 mt-1">
                      {productActivity.counts?.selectable_products_total ?? 0}
                    </div>
                  </div>
                  <div className="rounded-xl border p-4 bg-slate-50">
                    <div className="text-xs uppercase text-slate-500 tracking-wide">Статус конфигурации</div>
                    <div className="text-sm font-semibold text-slate-900 mt-1">
                      {productActivity.config?.has_explicit_config
                        ? 'Сохранён явный whitelist'
                        : 'Наследована из текущих исключений'}
                    </div>
                  </div>
                </div>
              )}

              {productActivity && (productActivity.counts?.ozon_hard_excluded ?? 0) > 0 && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  В Ozon архивировано / скрыто: <strong>{productActivity.counts?.ozon_hard_excluded}</strong> SKU.
                  Эти карточки нельзя активировать в дашборде — их нужно вернуть в продажу в админке Ozon.
                </div>
              )}
            </div>

            <div className="rounded-2xl border bg-white p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-base font-semibold text-gray-800">Синхронизация с Ozon</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Тянет archived / visibility / is_discounted из Seller API (/v3/product/list).
                    Архивированные на Ozon SKU автоматически становятся неактивными в платформе.
                    Обратного направления нет — неактивный в платформе товар не архивируется в Ozon.
                  </p>
                  {productActivity?.ozon_activity?.last_synced_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      Последняя синхронизация:{' '}
                      {new Date(productActivity.ozon_activity.last_synced_at).toLocaleString('ru')} ·
                      покрытие: {productActivity.ozon_activity.covered_skus ?? 0} SKU
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    syncOzonProductActivity().catch(console.error);
                  }}
                  disabled={productActivitySyncing}
                  className="shrink-0 rounded-lg border border-slate-900 px-4 py-2 text-sm font-medium text-slate-900 hover:bg-slate-100 disabled:border-slate-400 disabled:text-slate-400"
                >
                  {productActivitySyncing ? 'Запускается…' : 'Синхронизировать сейчас'}
                </button>
              </div>
            </div>

            <ActiveProductsModal
              open={productActivityModalOpen}
              products={productActivity?.products || []}
              initialActiveSkus={productActivity?.config?.active_skus || []}
              saving={productActivitySaving}
              activeCount={productActivity?.counts?.active_skus || 0}
              totalCount={productActivity?.counts?.selectable_products_total || 0}
              onClose={() => setProductActivityModalOpen(false)}
              onSave={saveProductActivity}
            />
          </div>
        )}

      </div>
    </div>
  );
};

export default SettingsPage;
